import React from 'react';
import PropTypes from 'prop-types';

/**
 * DocumentErrorBoundary - Specialized error boundary for document-related operations
 * 
 * This error boundary is specifically designed for document upload, preview, and
 * management components. It provides document-specific error messages and recovery options.
 */
class DocumentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    const errorId = `doc_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    this.logError(error, errorInfo);
    this.reportError(error, errorInfo);
  }

  logError = (error, errorInfo) => {
    const { errorId } = this.state;
    const { operation = 'Document Operation' } = this.props;
    
    if (import.meta.env.DEV) {
      console.group(`📄 Document Error Boundary - ${operation}`);
      console.error('Error ID:', errorId);
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo?.componentStack);
      console.groupEnd();
    }
  };

  reportError = (error, errorInfo) => {
    const { errorId } = this.state;
    const { operation = 'Document Operation' } = this.props;

    try {
      if (import.meta.env.PROD) {
        fetch('/api/errors/documents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            errorId,
            operation,
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo?.componentStack,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            type: 'document_error'
          })
        }).catch(() => {
          // Silent fail
        });
      }
    } catch {
      // Silent fail
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleSkipDocuments = () => {
    // Navigate away from document upload if possible
    if (this.props.onSkip) {
      this.props.onSkip();
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/claims';
    }
  };

  render() {
    if (this.state.hasError) {
      const { 
        operation = 'Document Upload', 
        customMessage,
        allowSkip = false 
      } = this.props;
      const { errorId, error } = this.state;

      return (
        <div className="min-h-[200px] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md border border-orange-200 p-6 text-center">
            {/* Document-specific error icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 mb-4">
              <svg 
                className="h-8 w-8 text-orange-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4m0 4h.01" 
                />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {operation} Issue
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              {customMessage || 
                `We're having trouble with ${operation.toLowerCase()}. This could be due to file size, format, or a temporary connection issue.`
              }
            </p>

            <div className="bg-orange-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-orange-700 font-medium mb-1">Troubleshooting Tips:</p>
              <ul className="text-xs text-orange-600 text-left space-y-1">
                <li>• Check file size (max 10MB)</li>
                <li>• Ensure file format is supported (PDF, JPG, PNG, DOC)</li>
                <li>• Try uploading one file at a time</li>
                <li>• Check your internet connection</li>
              </ul>
            </div>

            {errorId && (
              <div className="bg-gray-50 rounded p-2 mb-4">
                <p className="text-xs text-gray-500">
                  Reference: <span className="font-mono">{errorId}</span>
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </button>
              
              {allowSkip && (
                <button
                  onClick={this.handleSkipDocuments}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                >
                  Skip Documents
                </button>
              )}
            </div>

            {/* Development error details */}
            {import.meta.env.DEV && error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-gray-500 cursor-pointer">
                  Error Details (Dev)
                </summary>
                <div className="mt-2 p-2 bg-red-50 rounded text-xs font-mono text-red-700 max-h-32 overflow-auto">
                  {error.message}
                </div>
              </details>
            )}

            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Document upload issues?{' '}
                <a 
                  href="mailto:support@oldmutual.com?subject=Document%20Upload%20Issue" 
                  className="text-orange-600 underline"
                >
                  Contact IT Support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

DocumentErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  operation: PropTypes.string,
  customMessage: PropTypes.string,
  allowSkip: PropTypes.bool,
  onSkip: PropTypes.func,
};

DocumentErrorBoundary.defaultProps = {
  operation: 'Document Upload',
  customMessage: null,
  allowSkip: false,
  onSkip: null,
};

export default DocumentErrorBoundary;
